/**
 * Top-bar DOM factory.
 *
 * The v0.2.6 chrome follows amitone-design/studio-loaded.html:
 * IMAGE, FILE, and EXPORT are dropdown menus. Zoom, view, and settings remain
 * direct controls.
 */

import { openAboutPopup } from './about-popup';
import { createSettingsPopup, type SettingsPopupGpuOptions } from './settings-popup';

export interface TopBarI18n {
  t(key: string, params?: Record<string, string | number>): string;
  onLocaleChange(fn: () => void): void;
}

type ExportScale = '1' | '2' | '4';

export interface TopBarCallbacks {
  onFileChange(file: File): void;
  onMaskChange(file: File): void;
  onMaskClear?(): void;
  onProjectFileChange?(file: File): void;
  onProjectSave?(): void;
  onTemplateFileChange?(file: File): void;
  onTemplateSave?(): void;
  onPresetFileChange?(file: File): void;
  onPresetExport?(): void;
  onOverlayFileChange?(file: File): void;
  onOverlayClear?(): void;
  getIncludeReferenceOverlay?(): boolean;
  onIncludeReferenceOverlayChange?(enabled: boolean): void;
  onExportScaleChange?(scale: ExportScale): void;
  onExport?(): void;
  onCopy?(): void;
  onUndo?(): void;
  onRedo?(): void;
  onClear(): void;
  onFit(): void;
  onOneToOne(): void;
  onTwoHundred(): void;
  onZoomChange(percent: number): void;
  onViewToggle(): void;
  getSource(): { width: number; height: number } | null;
}

export interface TopBarHandles {
  element: HTMLElement;
  loadImageLabel: HTMLLabelElement;
  loadMaskLabel: HTMLLabelElement;
  fileInput: HTMLInputElement;
  maskInput: HTMLInputElement;
  projectLoadLabel: HTMLLabelElement;
  projectInput: HTMLInputElement;
  projectSaveButton: HTMLButtonElement;
  templateInput: HTMLInputElement;
  templateSaveButton: HTMLButtonElement;
  presetInput: HTMLInputElement;
  presetExportButton: HTMLButtonElement;
  overlayInput: HTMLInputElement;
  overlayClearButton: HTMLButtonElement;
  referenceOverlayMount: HTMLDivElement;
  referenceOverlayVisibilityMount: HTMLDivElement;
  clearMaskButton: HTMLButtonElement;
  undoButton: HTMLButtonElement;
  redoButton: HTMLButtonElement;
  clearButton: HTMLButtonElement;
  fitButton: HTMLButtonElement;
  oneToOneButton: HTMLButtonElement;
  zoomNumInput: HTMLInputElement;
  viewToggleBtn: HTMLButtonElement;
  syncLoadMaskEnabled(hasSource: boolean): void;
  syncViewToggleLabel(mode: 'result' | 'source'): void;
  syncZoomDisplay(percent: number): void;
  syncZoomMode(mode: 'fit' | '1:1' | '200%' | 'custom'): void;
  syncImageGated(hasImage: boolean): void;
  syncHistoryButtons(state: { canUndo: boolean; canRedo: boolean }): void;
  syncOutputActions(state: { canExport: boolean; scale?: ExportScale }): void;
  openMaskPicker(): void;
}

type MenuRoot = {
  root: HTMLDivElement;
  trigger: HTMLButtonElement;
  dropdown: HTMLDivElement;
};

export function createTopBar(
  i18n: TopBarI18n,
  callbacks: TopBarCallbacks,
  gpuOptions: SettingsPopupGpuOptions = {},
): TopBarHandles {
  const menuRoots: MenuRoot[] = [];
  let exportScale: ExportScale = '1';

  function makeSeparator(): HTMLDivElement {
    const sep = document.createElement('div');
    sep.className = 'top-bar-separator';
    sep.setAttribute('aria-hidden', 'true');
    return sep;
  }

  function setDisabled(
    el: HTMLButtonElement | HTMLInputElement | HTMLLabelElement,
    disabled: boolean,
  ): void {
    if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
      el.disabled = disabled;
    }
    if (disabled) {
      el.setAttribute('aria-disabled', 'true');
    } else {
      el.removeAttribute('aria-disabled');
    }
  }

  function attachPressState(label: HTMLLabelElement): void {
    const clear = (): void => {
      delete label.dataset['pressed'];
    };
    label.addEventListener('pointerdown', () => {
      label.dataset['pressed'] = 'true';
    });
    label.addEventListener('pointerup', clear);
    label.addEventListener('pointerleave', clear);
    label.addEventListener('pointercancel', clear);
    label.addEventListener('blur', clear);
  }

  function closeMenus(except?: MenuRoot): void {
    for (const menu of menuRoots) {
      if (menu === except) continue;
      menu.dropdown.hidden = true;
      menu.trigger.setAttribute('aria-expanded', 'false');
    }
  }

  function positionMenu(menu: MenuRoot): void {
    const rect = menu.trigger.getBoundingClientRect();
    const popupWidth = menu.dropdown.offsetWidth || 220;
    let left = rect.left;
    if (left + popupWidth > window.innerWidth - 8) {
      left = window.innerWidth - popupWidth - 8;
    }
    if (left < 8) left = 8;
    menu.dropdown.style.left = `${left}px`;
    menu.dropdown.style.top = `${rect.bottom + 4}px`;
  }

  function makeTopBarBtn(
    iconText: string,
    labelKey: string,
    ariaLabelKey?: string,
  ): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'top-bar-btn';

    const icon = document.createElement('span');
    icon.className = 'top-bar-btn-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = iconText;

    const label = document.createElement('span');
    label.className = 'top-bar-btn-label';
    label.dataset['i18n'] = labelKey;
    label.textContent = i18n.t(labelKey);

    btn.appendChild(icon);
    btn.appendChild(label);

    const sync = (): void => {
      const key = ariaLabelKey ?? labelKey;
      label.textContent = i18n.t(labelKey);
      btn.title = i18n.t(key);
      btn.setAttribute('aria-label', i18n.t(key));
    };
    sync();
    i18n.onLocaleChange(sync);

    return btn;
  }

  function makeHistoryButton(iconText: string, labelKey: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'top-bar-history-btn';

    const icon = document.createElement('span');
    icon.className = 'top-bar-btn-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = iconText;
    btn.appendChild(icon);

    const sync = (): void => {
      const label = i18n.t(labelKey);
      btn.title = label;
      btn.setAttribute('aria-label', label);
    };
    sync();
    i18n.onLocaleChange(sync);

    btn.addEventListener('click', () => {
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      onClick();
    });

    return btn;
  }

  function makeMenu(
    groupClass: string,
    id: string,
    labelKey: string,
    iconText: string,
  ): { group: HTMLDivElement; menu: MenuRoot } {
    const group = document.createElement('div');
    group.className = `top-bar-group ${groupClass}`;

    const root = document.createElement('div');
    root.className = 'file-menu-root';
    group.appendChild(root);

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.id = `${id}-menu-trigger`;
    trigger.className = 'top-bar-btn';
    trigger.setAttribute('aria-haspopup', 'menu');
    trigger.setAttribute('aria-expanded', 'false');

    const face = document.createElement('span');
    face.className = 'top-bar-btn-face';
    const icon = document.createElement('span');
    icon.className = 'top-bar-btn-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = iconText;
    const label = document.createElement('span');
    label.className = 'top-bar-btn-label';
    label.dataset['i18n'] = labelKey;
    label.textContent = i18n.t(labelKey);
    face.appendChild(icon);
    face.appendChild(label);
    trigger.appendChild(face);
    root.appendChild(trigger);

    const dropdown = document.createElement('div');
    dropdown.className = `file-menu-dropdown ${id}-menu-dropdown`;
    dropdown.setAttribute('role', 'menu');
    dropdown.setAttribute('aria-labelledby', trigger.id);
    dropdown.hidden = true;
    root.appendChild(dropdown);

    const menu: MenuRoot = { root, trigger, dropdown };
    menuRoots.push(menu);

    const sync = (): void => {
      const title = `${i18n.t(labelKey)} menu`;
      label.textContent = i18n.t(labelKey);
      trigger.title = title;
      trigger.setAttribute('aria-label', title);
    };
    sync();
    i18n.onLocaleChange(sync);

    trigger.addEventListener('click', (event) => {
      event.stopPropagation();
      const shouldOpen = dropdown.hidden;
      closeMenus(shouldOpen ? menu : undefined);
      dropdown.hidden = !shouldOpen;
      trigger.setAttribute('aria-expanded', String(shouldOpen));
      if (shouldOpen) positionMenu(menu);
    });

    return { group, menu };
  }

  function appendMenuSection(dropdown: HTMLElement, labelKey: string): HTMLDivElement {
    const section = document.createElement('div');
    section.className = 'file-menu-section';
    const label = document.createElement('div');
    label.className = 'file-menu-section-label';
    label.dataset['i18n'] = labelKey;
    label.textContent = i18n.t(labelKey);
    section.appendChild(label);
    dropdown.appendChild(section);
    i18n.onLocaleChange(() => {
      label.textContent = i18n.t(labelKey);
    });
    return section;
  }

  function appendDivider(dropdown: HTMLElement): void {
    const divider = document.createElement('div');
    divider.className = 'file-menu-divider';
    divider.setAttribute('aria-hidden', 'true');
    dropdown.appendChild(divider);
  }

  function appendNewBadge(parent: HTMLElement): void {
    const badge = document.createElement('span');
    badge.className = 'file-menu-item-new';
    badge.dataset['i18n'] = 'badge.new';
    badge.textContent = i18n.t('badge.new');
    badge.setAttribute('aria-label', 'New in v0.2.6');
    parent.appendChild(badge);
    i18n.onLocaleChange(() => {
      badge.textContent = i18n.t('badge.new');
    });
  }

  function fillMenuItemFace(
    item: HTMLElement,
    iconText: string,
    labelKey: string,
    newBadge = false,
  ): void {
    item.setAttribute('aria-label', i18n.t(labelKey));
    const face = document.createElement('span');
    face.className = 'top-bar-btn-face';
    const icon = document.createElement('span');
    icon.className = 'file-menu-item-icon top-bar-btn-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = iconText;
    const label = document.createElement('span');
    label.className = 'file-menu-item-label top-bar-btn-label';
    label.dataset['i18n'] = labelKey;
    label.textContent = i18n.t(labelKey);
    face.appendChild(icon);
    face.appendChild(label);
    item.appendChild(face);
    if (newBadge) appendNewBadge(item);
    i18n.onLocaleChange(() => {
      label.textContent = i18n.t(labelKey);
      item.setAttribute('aria-label', i18n.t(labelKey));
    });
  }

  function makeMenuButton(
    iconText: string,
    labelKey: string,
    onClick: () => void,
    newBadge = false,
  ): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'file-menu-item';
    btn.setAttribute('role', 'menuitem');
    btn.tabIndex = -1;
    fillMenuItemFace(btn, iconText, labelKey, newBadge);
    btn.addEventListener('click', () => {
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      closeMenus();
      onClick();
    });
    return btn;
  }

  function makeFileMenuItem(
    iconText: string,
    labelKey: string,
    accept: string,
    onFile: (file: File) => void,
    newBadge = false,
  ): { label: HTMLLabelElement; input: HTMLInputElement } {
    const label = document.createElement('label');
    label.className = 'file-menu-item';
    label.setAttribute('role', 'menuitem');
    label.tabIndex = -1;
    fillMenuItemFace(label, iconText, labelKey, newBadge);
    attachPressState(label);

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.className = 'sr-only';
    input.setAttribute('aria-label', i18n.t(labelKey));
    label.appendChild(input);

    i18n.onLocaleChange(() => {
      input.setAttribute('aria-label', i18n.t(labelKey));
    });

    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (file) onFile(file);
      closeMenus();
    });

    return { label, input };
  }

  function syncScaleButtons(scaleButtons: HTMLButtonElement[]): void {
    for (const button of scaleButtons) {
      button.setAttribute('aria-pressed', String(button.dataset['scale'] === exportScale));
    }
  }

  const topbar = document.createElement('header');
  topbar.className = 'top-bar';
  topbar.setAttribute('role', 'banner');

  function appendTopBarGroup(group: HTMLElement, options: { separatorBefore?: boolean } = {}): void {
    if (options.separatorBefore === true && topbar.querySelector('.top-bar-group') !== null) {
      topbar.appendChild(makeSeparator());
    }
    topbar.appendChild(group);
  }

  const brandGroup = document.createElement('div');
  brandGroup.className = 'top-bar-group top-bar-group-brand';
  appendTopBarGroup(brandGroup);

  const brandMark = document.createElement('div');
  brandMark.className = 'top-bar-brand';
  brandMark.setAttribute('aria-label', i18n.t('tooltip.logo'));
  brandMark.title = i18n.t('tooltip.logo');
  brandMark.setAttribute('aria-hidden', 'false');
  brandMark.style.cursor = 'pointer';
  brandMark.addEventListener('click', () => {
    openAboutPopup(brandMark);
  });
  const brandInner = document.createElement('div');
  brandInner.className = 'brand-mark';
  brandInner.setAttribute('aria-hidden', 'true');
  brandInner.textContent = 'A';
  brandMark.appendChild(brandInner);
  brandGroup.appendChild(brandMark);
  i18n.onLocaleChange(() => {
    brandMark.setAttribute('aria-label', i18n.t('tooltip.logo'));
    brandMark.title = i18n.t('tooltip.logo');
  });

  const menuGroup = document.createElement('div');
  menuGroup.className = 'top-bar-group top-bar-group-main-menus';
  appendTopBarGroup(menuGroup);

  const imageMenu = makeMenu('top-bar-group-image', 'image', 'topbar.menu.image', '⊡');
  menuGroup.appendChild(imageMenu.group);

  const mainImageSection = appendMenuSection(
    imageMenu.menu.dropdown,
    'topbar.section.mainImage',
  );
  const loadImage = makeFileMenuItem('↑', 'action.loadImage', 'image/*', (file) => {
    callbacks.onFileChange(file);
  });
  loadImage.input.id = 'sourceFileInput';
  mainImageSection.appendChild(loadImage.label);
  const clearButton = makeMenuButton('✕', 'action.clear', callbacks.onClear);
  setDisabled(clearButton, true);
  mainImageSection.appendChild(clearButton);

  appendDivider(imageMenu.menu.dropdown);
  const maskSection = appendMenuSection(imageMenu.menu.dropdown, 'topbar.section.mask');
  const loadMask = makeFileMenuItem('↑', 'action.loadMask', 'image/png', (file) => {
    if (callbacks.getSource() === null) {
      loadMask.input.value = '';
      return;
    }
    callbacks.onMaskChange(file);
  });
  setDisabled(loadMask.label, true);
  setDisabled(loadMask.input, true);
  maskSection.appendChild(loadMask.label);
  const editMaskButton = makeMenuButton('✎', 'action.editMask', () => {});
  editMaskButton.classList.add('file-menu-item--disabled');
  setDisabled(editMaskButton, true);
  appendNewBadge(editMaskButton);
  maskSection.appendChild(editMaskButton);
  const clearMaskButton = makeMenuButton('✕', 'action.clearMask', () => {
    callbacks.onMaskClear?.();
  });
  setDisabled(clearMaskButton, true);
  maskSection.appendChild(clearMaskButton);

  appendDivider(imageMenu.menu.dropdown);
  const overlaySection = appendMenuSection(
    imageMenu.menu.dropdown,
    'topbar.section.overlay',
  );
  const overlayLoad = makeFileMenuItem(
    '↑',
    'action.loadOverlay',
    'image/png,image/*',
    (file) => {
      if (callbacks.getSource() === null) {
        overlayLoad.input.value = '';
        return;
      }
      callbacks.onOverlayFileChange?.(file);
    },
    true,
  );
  setDisabled(overlayLoad.label, true);
  setDisabled(overlayLoad.input, true);
  overlaySection.appendChild(overlayLoad.label);
  const overlayClearButton = makeMenuButton(
    '✕',
    'action.clearOverlay',
    () => callbacks.onOverlayClear?.(),
    true,
  );
  setDisabled(overlayClearButton, true);
  overlaySection.appendChild(overlayClearButton);
  const referenceOverlayMount = document.createElement('div');
  referenceOverlayMount.className = 'file-menu-embedded reference-overlay-menu-mount';
  overlaySection.appendChild(referenceOverlayMount);

  const fileMenu = makeMenu('top-bar-group-file', 'file', 'topbar.menu.file', '▤');
  menuGroup.appendChild(fileMenu.group);

  const projectSection = appendMenuSection(fileMenu.menu.dropdown, 'topbar.section.project');
  const projectLoad = makeFileMenuItem(
    '↑',
    'action.openProject',
    '.amtn,application/zip,application/json',
    (file) => callbacks.onProjectFileChange?.(file),
    true,
  );
  projectLoad.input.id = 'projectFileInput';
  projectSection.appendChild(projectLoad.label);
  const projectSaveButton = makeMenuButton(
    '↓',
    'action.saveProject',
    () => callbacks.onProjectSave?.(),
    true,
  );
  setDisabled(projectSaveButton, true);
  projectSection.appendChild(projectSaveButton);

  appendDivider(fileMenu.menu.dropdown);
  const templateSection = appendMenuSection(
    fileMenu.menu.dropdown,
    'topbar.section.template',
  );
  const templateLoad = makeFileMenuItem(
    '↑',
    'action.openTemplate',
    '.amtt,application/zip,application/json',
    (file) => callbacks.onTemplateFileChange?.(file),
    true,
  );
  templateSection.appendChild(templateLoad.label);
  const templateSaveButton = makeMenuButton(
    '↓',
    'action.saveTemplate',
    () => callbacks.onTemplateSave?.(),
    true,
  );
  templateSection.appendChild(templateSaveButton);

  appendDivider(fileMenu.menu.dropdown);
  const presetSection = appendMenuSection(fileMenu.menu.dropdown, 'topbar.section.preset');
  const presetLoad = makeFileMenuItem(
    '↑',
    'action.importPreset',
    '.amtp,.json,application/zip,application/json',
    (file) => callbacks.onPresetFileChange?.(file),
  );
  presetSection.appendChild(presetLoad.label);
  const presetExportButton = makeMenuButton(
    '↓',
    'action.exportPreset',
    () => callbacks.onPresetExport?.(),
    true,
  );
  presetSection.appendChild(presetExportButton);

  const exportMenu = makeMenu('top-bar-group-export', 'export', 'topbar.menu.export', '↓');
  menuGroup.appendChild(exportMenu.group);

  const exportScaleButtons: HTMLButtonElement[] = [];
  const scaleSection = appendMenuSection(exportMenu.menu.dropdown, 'topbar.section.scale');
  const scaleWrap = document.createElement('div');
  scaleWrap.className = 'export-menu-scale';
  const scaleControl = document.createElement('div');
  scaleControl.className = 'segmented-control';
  scaleControl.setAttribute('role', 'group');
  scaleControl.setAttribute('aria-label', i18n.t('topbar.section.scale'));
  scaleWrap.appendChild(scaleControl);
  scaleSection.appendChild(scaleWrap);
  for (const [scale, key] of [
    ['1', 'export.scale.1x'],
    ['2', 'export.scale.2x'],
    ['4', 'export.scale.4x'],
  ] as const) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'segmented-control-btn';
    button.dataset['scale'] = scale;
    button.dataset['i18n'] = key;
    button.textContent = i18n.t(key);
    button.addEventListener('click', () => {
      if (button.disabled) return;
      exportScale = scale;
      syncScaleButtons(exportScaleButtons);
      callbacks.onExportScaleChange?.(scale);
    });
    exportScaleButtons.push(button);
    scaleControl.appendChild(button);
    i18n.onLocaleChange(() => {
      button.textContent = i18n.t(key);
    });
  }
  i18n.onLocaleChange(() => {
    scaleControl.setAttribute('aria-label', i18n.t('topbar.section.scale'));
  });
  syncScaleButtons(exportScaleButtons);

  appendDivider(exportMenu.menu.dropdown);
  const referenceExportSection = appendMenuSection(
    exportMenu.menu.dropdown,
    'export.referenceOverlay',
  );
  const referenceExportToggle = makeMenuButton(
    '▣',
    callbacks.getIncludeReferenceOverlay?.() ?? true
      ? 'export.referenceOverlay.include'
      : 'export.referenceOverlay.exclude',
    () => {},
  );
  function syncReferenceExportToggle(): void {
    const enabled = callbacks.getIncludeReferenceOverlay?.() ?? true;
    const label = referenceExportToggle.querySelector<HTMLElement>('.file-menu-item-label');
    const key = enabled ? 'export.referenceOverlay.include' : 'export.referenceOverlay.exclude';
    if (label) {
      label.dataset['i18n'] = key;
      label.textContent = i18n.t(key);
    }
    referenceExportToggle.setAttribute('aria-pressed', String(enabled));
    referenceExportToggle.setAttribute('aria-label', i18n.t(key));
  }
  referenceExportToggle.addEventListener('click', (event) => {
    event.stopPropagation();
    const next = !(callbacks.getIncludeReferenceOverlay?.() ?? true);
    callbacks.onIncludeReferenceOverlayChange?.(next);
    syncReferenceExportToggle();
  });
  referenceExportSection.appendChild(referenceExportToggle);
  syncReferenceExportToggle();
  i18n.onLocaleChange(syncReferenceExportToggle);

  appendDivider(exportMenu.menu.dropdown);
  const exportActionSection = appendMenuSection(
    exportMenu.menu.dropdown,
    'topbar.section.exportActions',
  );
  exportActionSection.querySelector('.file-menu-section-label')?.remove();
  const exportPngButton = makeMenuButton('↓', 'action.exportPng', () => callbacks.onExport?.());
  setDisabled(exportPngButton, true);
  exportActionSection.appendChild(exportPngButton);
  const copyPngButton = makeMenuButton('⧉', 'action.copyPng', () => callbacks.onCopy?.());
  setDisabled(copyPngButton, true);
  exportActionSection.appendChild(copyPngButton);

  const historyGroup = document.createElement('div');
  historyGroup.className = 'top-bar-group top-bar-group-history';
  const undoButton = makeHistoryButton('↶', 'action.undo', () => callbacks.onUndo?.());
  setDisabled(undoButton, true);
  historyGroup.appendChild(undoButton);
  const redoButton = makeHistoryButton('↷', 'action.redo', () => callbacks.onRedo?.());
  setDisabled(redoButton, true);
  historyGroup.appendChild(redoButton);
  appendTopBarGroup(historyGroup, { separatorBefore: true });

  const zoomGroup = document.createElement('div');
  zoomGroup.className = 'top-bar-group top-bar-group-zoom';
  appendTopBarGroup(zoomGroup, { separatorBefore: true });

  const fitButton = makeTopBarBtn('⛶', 'view.fit');
  setDisabled(fitButton, true);
  zoomGroup.appendChild(fitButton);
  fitButton.addEventListener('click', callbacks.onFit);

  const oneToOneButton = makeTopBarBtn('□', 'view.1to1');
  setDisabled(oneToOneButton, true);
  zoomGroup.appendChild(oneToOneButton);
  oneToOneButton.addEventListener('click', callbacks.onOneToOne);

  const zoomNumInput = document.createElement('input');
  zoomNumInput.type = 'number';
  zoomNumInput.min = '5';
  zoomNumInput.max = '3200';
  zoomNumInput.step = '1';
  zoomNumInput.className = 'zoom-num';
  zoomNumInput.value = '100';
  zoomNumInput.setAttribute('aria-label', i18n.t('zoom.label'));
  setDisabled(zoomNumInput, true);
  i18n.onLocaleChange(() => {
    zoomNumInput.setAttribute('aria-label', i18n.t('zoom.label'));
  });
  zoomNumInput.addEventListener('change', () => {
    const percent = Number(zoomNumInput.value);
    if (!Number.isFinite(percent) || percent <= 0) return;
    callbacks.onZoomChange(percent);
  });

  const pctSuffix = document.createElement('span');
  pctSuffix.className = 'pct-suffix';
  pctSuffix.setAttribute('aria-hidden', 'true');
  pctSuffix.textContent = '%';

  const zoomStepper = document.createElement('span');
  zoomStepper.className = 'stepper-buttons';
  const stepZoom = (direction: -1 | 1): void => {
    const current = Number(zoomNumInput.value);
    const min = Number(zoomNumInput.min);
    const max = Number(zoomNumInput.max);
    const base = Number.isFinite(current) ? current : 100;
    const next = Math.min(max, Math.max(min, Math.round(base) + direction));
    zoomNumInput.value = String(next);
    zoomNumInput.dispatchEvent(new Event('change', { bubbles: true }));
  };
  const zoomUp = document.createElement('button');
  zoomUp.type = 'button';
  zoomUp.className = 'step-button step-up';
  zoomUp.setAttribute('aria-label', 'Increase zoom');
  setDisabled(zoomUp, true);
  zoomUp.addEventListener('click', (event) => {
    event.preventDefault();
    stepZoom(1);
  });
  const zoomDown = document.createElement('button');
  zoomDown.type = 'button';
  zoomDown.className = 'step-button step-down';
  zoomDown.setAttribute('aria-label', 'Decrease zoom');
  setDisabled(zoomDown, true);
  zoomDown.addEventListener('click', (event) => {
    event.preventDefault();
    stepZoom(-1);
  });
  zoomStepper.appendChild(zoomUp);
  zoomStepper.appendChild(zoomDown);

  const zoomNumCluster = document.createElement('span');
  zoomNumCluster.className = 'zoom-num-cluster';
  zoomNumCluster.appendChild(zoomNumInput);
  zoomNumCluster.appendChild(pctSuffix);
  zoomNumCluster.appendChild(zoomStepper);
  zoomGroup.appendChild(zoomNumCluster);

  const viewGroup = document.createElement('div');
  viewGroup.className = 'top-bar-group top-bar-group-view';
  appendTopBarGroup(viewGroup, { separatorBefore: true });

  const viewToggleBtn = document.createElement('button');
  viewToggleBtn.type = 'button';
  viewToggleBtn.className = 'top-bar-btn top-bar-view-toggle';
  setDisabled(viewToggleBtn, true);
  viewToggleBtn.setAttribute('aria-label', i18n.t('aria.viewMode.toggle'));
  viewToggleBtn.title = i18n.t('aria.viewMode.toggle');
  const viewIcon = document.createElement('span');
  viewIcon.className = 'top-bar-btn-icon';
  viewIcon.setAttribute('aria-hidden', 'true');
  viewIcon.textContent = '◧';
  const viewLabel = document.createElement('span');
  viewLabel.className = 'top-bar-btn-label';
  viewLabel.dataset['i18n'] = 'view.result';
  viewLabel.textContent = i18n.t('view.result');
  viewToggleBtn.appendChild(viewIcon);
  viewToggleBtn.appendChild(viewLabel);
  i18n.onLocaleChange(() => {
    viewToggleBtn.setAttribute('aria-label', i18n.t('aria.viewMode.toggle'));
    viewToggleBtn.title = i18n.t('aria.viewMode.toggle');
    viewLabel.textContent = i18n.t(viewLabel.dataset['i18n'] ?? 'view.result');
  });
  viewGroup.appendChild(viewToggleBtn);
  viewToggleBtn.addEventListener('click', callbacks.onViewToggle);

  const referenceOverlayVisibilityMount = document.createElement('div');
  referenceOverlayVisibilityMount.className = 'reference-overlay-visibility-mount';
  viewGroup.appendChild(referenceOverlayVisibilityMount);

  const spacer = document.createElement('div');
  spacer.className = 'top-bar-spacer';
  topbar.appendChild(spacer);

  const settingsSection = document.createElement('div');
  settingsSection.className = 'top-bar-group top-bar-group-settings top-bar-settings-section';
  appendTopBarGroup(settingsSection);
  createSettingsPopup(settingsSection, gpuOptions);

  const closeOnOutsideClick = (event: MouseEvent): void => {
    const target = event.target as Node;
    if (!topbar.contains(target)) closeMenus();
  };
  const closeOnEscape = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') closeMenus();
  };
  const repositionOpenMenus = (): void => {
    for (const menu of menuRoots) {
      if (!menu.dropdown.hidden) positionMenu(menu);
    }
  };
  document.addEventListener('click', closeOnOutsideClick, true);
  document.addEventListener('keydown', closeOnEscape, true);
  window.addEventListener('resize', repositionOpenMenus);
  window.addEventListener('scroll', repositionOpenMenus, { capture: true });

  function syncLoadMaskEnabled(hasSource: boolean): void {
    setDisabled(loadMask.label, !hasSource);
    setDisabled(loadMask.input, !hasSource);
    setDisabled(clearMaskButton, !hasSource);
  }

  function syncViewToggleLabel(mode: 'result' | 'source'): void {
    const key = mode === 'result' ? 'view.original' : 'view.result';
    viewLabel.dataset['i18n'] = key;
    viewLabel.textContent = i18n.t(key);
    viewToggleBtn.setAttribute('aria-pressed', mode === 'source' ? 'true' : 'false');
  }

  function syncZoomDisplay(percent: number): void {
    zoomNumInput.value = String(Math.round(percent));
  }

  function syncZoomMode(mode: 'fit' | '1:1' | '200%' | 'custom'): void {
    const hasSource = callbacks.getSource() !== null;
    fitButton.setAttribute('aria-pressed', String(hasSource && mode === 'fit'));
    oneToOneButton.setAttribute('aria-pressed', String(hasSource && mode === '1:1'));
  }

  function syncImageGated(hasImage: boolean): void {
    setDisabled(fitButton, !hasImage);
    setDisabled(oneToOneButton, !hasImage);
    setDisabled(zoomNumInput, !hasImage);
    setDisabled(zoomUp, !hasImage);
    setDisabled(zoomDown, !hasImage);
    setDisabled(viewToggleBtn, !hasImage);
    setDisabled(projectSaveButton, !hasImage);
    setDisabled(clearButton, !hasImage);
    setDisabled(overlayLoad.label, !hasImage);
    setDisabled(overlayLoad.input, !hasImage);
    setDisabled(overlayClearButton, !hasImage);
    syncLoadMaskEnabled(hasImage);
  }

  function syncHistoryButtons(state: { canUndo: boolean; canRedo: boolean }): void {
    setDisabled(undoButton, !state.canUndo);
    setDisabled(redoButton, !state.canRedo);
  }

  function syncOutputActions(state: { canExport: boolean; scale?: ExportScale }): void {
    if (state.scale) exportScale = state.scale;
    syncScaleButtons(exportScaleButtons);
    for (const button of exportScaleButtons) {
      setDisabled(button, !state.canExport);
    }
    setDisabled(exportPngButton, !state.canExport);
    setDisabled(copyPngButton, !state.canExport);
  }

  function openMaskPicker(): void {
    loadMask.input.click();
  }

  return {
    element: topbar,
    loadImageLabel: loadImage.label,
    loadMaskLabel: loadMask.label,
    fileInput: loadImage.input,
    maskInput: loadMask.input,
    projectLoadLabel: projectLoad.label,
    projectInput: projectLoad.input,
    projectSaveButton,
    templateInput: templateLoad.input,
    templateSaveButton,
    presetInput: presetLoad.input,
    presetExportButton,
    overlayInput: overlayLoad.input,
    overlayClearButton,
    referenceOverlayMount,
    referenceOverlayVisibilityMount,
    clearMaskButton,
    undoButton,
    redoButton,
    clearButton,
    fitButton,
    oneToOneButton,
    zoomNumInput,
    viewToggleBtn,
    syncLoadMaskEnabled,
    syncViewToggleLabel,
    syncZoomDisplay,
    syncZoomMode,
    syncImageGated,
    syncHistoryButtons,
    syncOutputActions,
    openMaskPicker,
  };
}
